import React, { useState } from 'react';
import { DatePicker } from 'antd';
import type { DatePickerProps } from 'antd';
import { Dayjs } from 'dayjs';
import { formatDateWithLocale, renderDateWithLocale } from 'utils/date-time';
import { DATE_FORMAT, DATE_FORMAT_MASK, dateLanguagePlaceholder, DATE_FORMAT_MONTH_YEAR } from 'configs/constants';
import { useTranslation } from 'react-i18next';

interface CustomDatePickerProps extends Omit<DatePickerProps, 'value' | 'onChange'> {
  value?: Dayjs;
  onChange?: (date: Dayjs | null, dateString: string | string[]) => void;
  placeholder?: string;
  disabledDate?: (current: Dayjs) => boolean;
  className?: string;
  useCustomPopupStyle?: boolean;
  useCustomPopupContainer?: boolean;
}

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  value,
  onChange,
  placeholder,
  disabledDate,
  className = 'w-full',
  useCustomPopupStyle = false,
  useCustomPopupContainer = false,
  ...otherProps
}) => {
  const { i18n } = useTranslation();
  const language = i18n?.language || 'en';

  const [showTranslated, setShowTranslated] = useState(true);

  // Determine picker type
  const picker = otherProps.picker;
  // Use month/year format if picker is 'month', else use default
  const dateFormat = picker === 'month' ? DATE_FORMAT_MONTH_YEAR : DATE_FORMAT;
  const dateFormatMask = picker === 'month' ? DATE_FORMAT_MONTH_YEAR : DATE_FORMAT_MASK;

  const handleChange = (date: Dayjs | null, dateString: string | string[]) => {
    onChange?.(date, dateString);
  };

  const handleFocus = () => {
    setShowTranslated(false);
  };

  const handleBlur = () => {
    setShowTranslated(true);
  };

  const formatFunction = showTranslated && value ? () => formatDateWithLocale(value, dateFormat) : dateFormatMask;
  const placeholderKey = picker === 'month' ? `${language}_month` : language;

  return (
    <DatePicker
      {...otherProps}
      className={className}
      value={value}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={
        showTranslated ? placeholder : dateLanguagePlaceholder[placeholderKey as keyof typeof dateLanguagePlaceholder]
      }
      disabledDate={disabledDate}
      format={formatFunction}
      cellRender={renderDateWithLocale}
      allowClear
      {...(useCustomPopupStyle && {
        popupStyle: { position: 'absolute', top: '100%' },
      })}
      {...(useCustomPopupContainer && {
        getPopupContainer: (trigger: HTMLElement) => trigger.parentNode as HTMLElement,
      })}
    />
  );
};

export default CustomDatePicker;
